import React, { useContext } from 'react';
import { Dialog, DialogContent, DialogActions, Button, DialogTitle, makeStyles, Theme } from '@material-ui/core';
import { useGenericDialogStyle, useScreenSizeCheck, capitalizeAll, FontIcon, capitalizeFirst } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { useRitzTranslation } from '../../utils/customHooks';
import { LanguageSelectorContainer } from '../language-selector/LanguageSelectorContainer';
import { getDashboardWidgetTypeDescription, getDashboardWidgetTypeIcon, getDashboardWidgetTypeName } from './dashboardUtils';
import { DashboardWidgetType } from './dashboardTypes';


const useStyle = makeStyles((theme: Theme) => {
    return {
        widgetTypeList: {
            width         : '100%',
            display       : 'flex',
            flexDirection : 'column',
            alignItems    : 'center',
            justifyContent: 'center',
        },

        widgetType: {
            margin        : theme.spacing(1, 0),
            width         : '100%',
            display       : 'flex',
            flexDirection : 'row',
            alignItems    : 'center',
            justifyContent: 'center',
            cursor        : 'pointer',
            color         : theme.palette.text.secondary,
            '&:hover'     : {
                color: theme.palette.primary.main,
            },
        },

        widgetTypeView: {
            marginRight: theme.spacing(2),
            '& .icon'  : {
                fontSize: 50,
                width   : 50,
            },
        },

        widgetTypeInfo: {
            flex      : 1,
            '& .title': {
                fontWeight: 'bold',
            },
            '& .description': {
            },
        },
    };
}, { name: 'SelectDashboardWidgetTypeDialog' });



export interface SelectDashboardWidgetTypeDialogProps {
    readonly projectId: number | undefined;
    readonly open: boolean;
    readonly excludedWidgetTypes?: readonly DashboardWidgetType[] | undefined;
    readonly onCancel: ()=> void;
    readonly onWidgetTypeSelected: (type: DashboardWidgetType)=> void;
}



/**
 * This is a dialog used for creating a request to get a device's log
 */
export const SelectDashboardWidgetTypeDialog: React.FC<SelectDashboardWidgetTypeDialogProps> = (
    props: SelectDashboardWidgetTypeDialogProps,
) => {

    const { state:dataState } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const { trans } = useRitzTranslation(project);

    const dialogClasses = useGenericDialogStyle();
    const classes = useStyle();
    const isMediumScreen = useScreenSizeCheck('md');


    return (
        <Dialog
            open={props.open}
            fullScreen={!isMediumScreen}
            fullWidth={isMediumScreen}
            disableEscapeKeyDown={false}
            onClose={(event, reason) => {
                // Don't close the dialog when backdrop clicked
                if (reason !== 'backdropClick') {
                    props.onCancel();
                }
            }}
        >
            <DialogTitle className={dialogClasses.dialogTitle}>
                <div className={dialogClasses.dialogTitleText}>
                    {capitalizeAll(trans('dialog.select_dashboard_widget_type.title'))}
                </div>
                <LanguageSelectorContainer projectId={props.projectId} minimal />
            </DialogTitle>

            <DialogContent
                className={dialogClasses.dialogContent}
            >
                <div className={classes.widgetTypeList}>
                    {Object.values(DashboardWidgetType).filter((type) => {
                        return !props.excludedWidgetTypes || !props.excludedWidgetTypes.includes(type);
                    }).map((type) => {
                        return (
                            <div
                                key={type}
                                className={classes.widgetType}
                                onClick={() => {
                                    props.onWidgetTypeSelected(type);
                                }}
                            >
                                <div className={classes.widgetTypeView}>
                                    <FontIcon className="icon" iconName={getDashboardWidgetTypeIcon(type)} />
                                </div>
                                <div className={classes.widgetTypeInfo}>
                                    <div className="title">{capitalizeFirst(trans(getDashboardWidgetTypeName(type)))}</div>
                                    <div className="description">{capitalizeFirst(trans(getDashboardWidgetTypeDescription(type)))}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </DialogContent>

            <DialogActions
                className={dialogClasses.dialogActionContainer}
            >
                <Button
                    onClick={props.onCancel}
                >
                    {trans('label.cancel')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
